"use client";

import React from "react";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { format, parseISO } from "date-fns";
import { ImageType } from "@/types/baseTypes";
import { customSanitize } from "@/utils/customSanitize";
import { getDateFnsLocale } from "@/utils/getDateFnsLocale";
import { Button } from "@/components/Button/Button";
import { MediaItem } from "@/components/MediaItem/MediaItem";
import styles from "./HighlightCard.module.scss";
import CardLinkOverlay from "../CardLinkOverlay/CardLinkOverlay";

export type HighlightCardElement = {
  title: string;
  subline: string;
  excerpt: string;
  image: ImageType | false;
  link: string;
  date: string;
  end_date: string;
  text_bubble?: string;
};

interface HighlightCardProps {
  element: HighlightCardElement;
  fallbackImage: ImageType | false | undefined;
  headingLevel: number;
  skeleton?: boolean;
  type: "highlight" | "event";
  isPast?: boolean;
}

const getDateTimeValue = (date: string) => {
  const dateParts = date.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);

  if (!dateParts) {
    return undefined;
  }

  const [, day, month, year] = dateParts;

  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

const formatCardDate = (dateTime: string, showYear: boolean, locale: string) =>
  format(parseISO(dateTime), showYear ? "dd. MMM yyyy" : "dd. MMM", {
    locale: getDateFnsLocale(locale),
  });

export const HighlightCard = ({
  element,
  fallbackImage,
  headingLevel,
  skeleton = false,
  type = "highlight",
  isPast = false,
}: HighlightCardProps) => {
  const { getLabel, locale } = useGlobalContext();
  const skeletonElementClass = skeleton ? "skeleton-element" : "";
  const dateTime = getDateTimeValue(element.date);
  const endDateTime = getDateTimeValue(element.end_date);
  const hasDate = Boolean(element.date);
  const dateYearHidden =
    dateTime &&
    endDateTime &&
    new Date(dateTime).getFullYear() === new Date(endDateTime).getFullYear();
  const hasEndDate = Boolean(element.end_date);

  return (
    <article
      className={`${styles["highlight-card"]} ${isPast ? styles["is-past"] : ""} is-box`}
    >
      <div className={styles["media-wrapper"]}>
        <MediaItem
          image_desktop={element.image || fallbackImage || false}
          sizes="(max-width: 767px) 100vw, (max-width: 1215px) 50vw, 25vw"
          objectFit="cover"
          className={`${styles["media"]} ${skeletonElementClass}`}
          showMediaInfo={false}
          link={
            element.link
              ? {
                  url: element.link,
                  target: "_self",
                  title: "",
                }
              : false
          }
        />
        {element.text_bubble ? (
          <div
            className={`${styles["date"]} ${styles["is-text-bubble"]} ${skeletonElementClass}`}
          >
            <span className={styles["text-bubble"]}>{element.text_bubble}</span>
          </div>
        ) : (
          (hasDate || hasEndDate) && (
            <div className={`${styles["date"]} ${skeletonElementClass}`}>
              {hasDate && dateTime && (
                <time dateTime={dateTime}>
                  {formatCardDate(dateTime, !dateYearHidden, locale)}
                </time>
              )}
              {hasDate && hasEndDate && (
                <span aria-hidden={true} className={styles["date-separator"]}>
                  –
                </span>
              )}
              {hasEndDate && endDateTime && (
                <time dateTime={endDateTime}>
                  {formatCardDate(endDateTime, true, locale)}
                </time>
              )}
            </div>
          )
        )}
      </div>
      <div className={styles["content"]}>
        <header>
          <p
            role="heading"
            aria-level={headingLevel}
            className={`${styles["title"]} ${skeletonElementClass} pb-0`}
            dangerouslySetInnerHTML={{ __html: customSanitize(element.title) }}
          />
          {element.subline && (
            <p
              className={`${styles["subline"]} ${skeletonElementClass}`}
              dangerouslySetInnerHTML={{
                __html: customSanitize(element.subline),
              }}
            />
          )}
          {element.excerpt && (
            <p
              className={`${styles["excerpt"]} ${skeletonElementClass}`}
              dangerouslySetInnerHTML={{
                __html: customSanitize(element.excerpt),
              }}
            />
          )}
        </header>
        {element.link && (
          <footer className={styles["footer"]}>
            <Button
              type="secondary"
              aria-label={getLabel("aria_label_postlink").replace(
                `"$posttitle"`,
                element.title,
              )}
              href={element.link}
              target="_self"
              className={`${styles["link"]} ${skeletonElementClass} my-0`}
            >
              {getLabel(`${type}_link_label`, "Zum Highlight")}
            </Button>
          </footer>
        )}
      </div>
      <CardLinkOverlay getLabel={getLabel} link={element.link} />
    </article>
  );
};
